import { NavLink, useNavigate, Outlet } from "react-router-dom";
import { useAuth, ROLE_LABEL, canAccess } from "../context/AuthContext";
import { LayoutDashboard, Package, ShoppingCart, FileText, Receipt, Landmark, Users, LogOut, Building2, Truck, Menu, X, Settings as SettingsIcon, BookOpen, FileSpreadsheet, Wallet, UserSquare, ClipboardList } from "lucide-react";
import { useState } from "react";
import { Button } from "./ui/button";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, module: "dashboard" },
  { to: "/inventory", label: "Inventory / Gudang", icon: Package, module: "inventory" },
  { to: "/suppliers", label: "Supplier", icon: Truck, module: "suppliers" },
  { to: "/purchasing", label: "Purchasing", icon: ShoppingCart, module: "purchasing" },
  { to: "/customers", label: "Customer", icon: Building2, module: "customers" },
  { to: "/quotations", label: "Quotation", icon: ClipboardList, module: "quotations" },
  { to: "/sales", label: "Sales Order", icon: FileText, module: "sales" },
  { to: "/invoices", label: "Invoice", icon: Receipt, module: "invoices" },
  { to: "/finance", label: "Finance", icon: Landmark, module: "finance" },
  { to: "/expenses", label: "Beban Operasional", icon: Wallet, module: "expenses" },
  { to: "/personalia", label: "Personalia (HR)", icon: UserSquare, module: "personalia" },
  { to: "/reports", label: "Laporan Keuangan", icon: BookOpen, module: "reports" },
  { to: "/efaktur", label: "e-Faktur DJP", icon: FileSpreadsheet, module: "efaktur" },
  { to: "/users", label: "User Management", icon: Users, module: "users" },
  { to: "/settings", label: "Profil Perusahaan", icon: SettingsIcon, module: "settings" },
];

export default function AppLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const items = NAV.filter((n) => canAccess(user.role, n.module));

  const handleLogout = () => { logout(); nav("/login"); };

  return (
    <div className="min-h-screen flex bg-slate-50">
      {/* Sidebar */}
      <aside className={`fixed lg:sticky top-0 h-screen z-40 w-72 bg-navy-900 border-r border-navy-700/40 flex flex-col transition-transform ${open ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}>
        <div className="px-6 py-6 border-b border-white/5 flex items-center gap-3">
          <div className="h-11 w-11 rounded-xl bg-gradient-to-br from-yellow-400 to-yellow-600 flex items-center justify-center shadow-lg">
            <span className="font-display font-extrabold text-navy-900 text-lg">N</span>
          </div>
          <div>
            <div className="font-display font-bold text-white text-lg tracking-tight">Fuji Indo Surya</div>
            <div className="text-[11px] uppercase tracking-widest text-yellow-500/80 font-medium">General Trading</div>
          </div>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto" data-testid="sidebar-nav">
          {items.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              onClick={() => setOpen(false)}
              className={({ isActive }) => `sidebar-link ${isActive ? "active" : ""}`}
              data-testid={`nav-${it.module}`}
            >
              <it.icon size={18} />
              <span>{it.label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-white/5">
          <div className="flex items-center gap-3 px-2 py-2">
            <div className="h-9 w-9 rounded-full bg-yellow-500/20 text-yellow-400 flex items-center justify-center font-display font-bold">
              {(user.full_name || user.employee_id).charAt(0).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-white text-sm font-semibold truncate" data-testid="user-name">{user.full_name}</div>
              <div className="text-[11px] text-yellow-500/80 font-mono">{user.employee_id} · {ROLE_LABEL[user.role]}</div>
            </div>
          </div>
          <Button onClick={handleLogout} variant="ghost" className="w-full mt-2 text-white/70 hover:text-white hover:bg-white/5 justify-start" data-testid="logout-btn">
            <LogOut size={16} className="mr-2" /> Keluar
          </Button>
        </div>
      </aside>

      {open && <div className="fixed inset-0 bg-black/40 z-30 lg:hidden" onClick={() => setOpen(false)} />}

      <div className="flex-1 min-w-0 flex flex-col">
        <header className="sticky top-0 z-20 bg-white/95 backdrop-blur border-b border-slate-200 px-4 sm:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => setOpen(true)} className="lg:hidden p-2 rounded-lg hover:bg-slate-100" data-testid="menu-toggle">
              {open ? <X size={20} /> : <Menu size={20} />}
            </button>
            <div>
              <div className="text-[11px] font-medium uppercase tracking-widest text-slate-400">ERP System</div>
              <div className="font-display font-bold text-slate-900 text-lg">Sistem ERP Terintegrasi</div>
            </div>
          </div>
          <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-full bg-slate-100 border border-slate-200">
            <span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
            <span className="text-xs font-medium text-slate-600">Sistem Aktif</span>
          </div>
        </header>
        <main className="flex-1 p-4 sm:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
